'use client';

import { createContext, useContext, useCallback } from 'react';
import type { ReactNode } from 'react';

interface ScrollManagerContextValue {
  saveScroll: (key: string, position: number) => void;
  getScroll: (key: string) => number;
  clearScroll: (key: string) => void;
  clearAll: () => void;
}

const ScrollManagerContext = createContext<ScrollManagerContextValue | undefined>(undefined);

// WHY: переход из экрана в вику и обратно на вебе меняет каркас страницы, и провайдер монтируется заново — позиции живут
// дольше него, иначе «Назад» из вики вернул бы сетку кейса к началу
const scrollPositions = new Map<string, number>();

export const ScrollManagerProvider = ({ children }: { children: ReactNode }) => {
  const saveScroll = useCallback((key: string, position: number) => {
    scrollPositions.set(key, position);
  }, []);

  const getScroll = useCallback((key: string): number => {
    return scrollPositions.get(key) ?? 0;
  }, []);

  const clearScroll = useCallback((key: string) => {
    scrollPositions.delete(key);
  }, []);

  const clearAll = useCallback(() => {
    scrollPositions.clear();
  }, []);

  return (
    <ScrollManagerContext.Provider value={{ saveScroll, getScroll, clearScroll, clearAll }}>
      {children}
    </ScrollManagerContext.Provider>
  );
};

const NO_OP: ScrollManagerContextValue = {
  saveScroll: () => {},
  getScroll: () => 0,
  clearScroll: () => {},
  clearAll: () => {},
};

export const useScrollManager = (): ScrollManagerContextValue => {
  return useContext(ScrollManagerContext) ?? NO_OP;
};
